import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppModule } from './app/app.module';
import * as Sentry from '@sentry/angular';
import { environment } from './environments/environment';

if (environment.production) Sentry.init({
  dsn: 'https://925131827cec0bddae2e88719875d917@o4504730795507712.ingest.sentry.io/4506191052800000',

  // Enhanced trace propagation targets - enables distributed tracing between frontend and backend
  tracePropagationTargets: [
    'localhost',
    /^\//,
    /^https:\/\/simserver-prod\.quantified\.ai/,
    /^https:\/\/simserver-prod-eu\.quantified\.ai/,
    /^https:\/\/simserver-qa\.quantified\.ai/,
    /^https:\/\/simserver-preprod\.quantified\.ai/,
    /^https:\/\/simadmin-prod\.quantified\.ai/,
    /^https:\/\/simadmin-prod-eu\.quantified\.ai/,
    /^https:\/\/simadmin-qa\.quantified\.ai/,
    /^https:\/\/simadmin-preprod\.quantified\.ai/,
  ],

  integrations: [
    // Browser tracing for performance monitoring with Angular routing
    Sentry.browserTracingIntegration({
      // Enable automatic instrumentation of Angular router
      enableInp: true, // Enable Interaction to Next Paint (INP) - replaces FID in v10
    }),
    // Session Replay for debugging user sessions
    Sentry.replayIntegration({
      // Privacy settings for replay
      maskAllText: false,
      blockAllMedia: false,
    }),
    // Captures warn + error from console — targets third-party libraries and Angular internals
    // that call console.error/warn directly and never go through our logger.error() or captureException().
    // Our own code (logger.error, ErrorHandlingService) gates console.error behind !isProduction,
    // so in production only third-party and framework errors reach this — no duplicates from our code.
    Sentry.captureConsoleIntegration({
      levels: ['warn', 'error']
    }),
    // Browser profiling - captures JavaScript call stacks to identify performance bottlenecks
    // Shows which functions consume the most CPU time and helps optimize slow code paths
    Sentry.browserProfilingIntegration(),
  ],

  // Performance Monitoring — route-aware sampler replaces the global tracesSampleRate scalar.
  // The sampler fires once per transaction (pageload / navigation / HTTP) and returns a rate 0.0–1.0.
  //
  // Key rule: any transaction whose op is 'call.simulation' is our manually-opened call-session
  // span (see SentryService.startCallSession). Returning 1.0 for it ensures every HTTP call,
  // WebSocket event and child span recorded during an active simulation is captured with full
  // fidelity. All other route navigations are sampled at a low rate to control costs.
  //
  // NOTE: tracesSampleRate and tracesSampler are mutually exclusive — Sentry ignores
  // tracesSampleRate when tracesSampler is present.
  tracesSampler: (samplingContext) => {
    // Always trace our manually-opened call-session transaction at 100%
    if (samplingContext['transactionContext']?.op === 'call.simulation') return 1.0;

    // Propagate parent sampling decision for HTTP child spans so distributed traces stay coherent
    if (samplingContext.parentSampled !== undefined) return samplingContext.parentSampled;

    // All other route navigations (home, assignments, results, reporting, etc.)
    return 0.05;
  },

  // Session Replay — both automatic rates are set to 0 to disable Sentry's built-in
  // session sampling. Replay is started and stopped manually via SentryService.startReplay()
  // and SentryService.stopReplay(), which are called by CallComponent.ngOnInit / ngOnDestroy.
  // This means replay is recorded ONLY during an active call/simulation — not on home, results,
  // assignments, or any other route — which eliminates the storage cost of recording full
  // non-critical user sessions.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,

  // Profiling - Controls what % of transactions include performance profiling data
  profilesSampleRate: 1.0,

  // Environment configuration
  environment: environment.envName,

  // Control IP address collection (required in v10 for IP tracking)
  sendDefaultPii: false, // Set to true if you need to collect user IP addresses

  // normalizeDepth: Controls how deeply nested objects are serialized when sent to Sentry
  // Higher values = more detail but larger payload size
  // Example: User object with nested properties (user.profile.address.city)
  // - Depth 3 would capture up to user.profile.address
  // - Depth 10 captures deeper nested structures
  normalizeDepth: 3,
});


// Disable console logs in production
if (environment.production) {
  window.console.log = () => {};
}

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));
